import { dispatchSessionGitResult } from "@/lib/session-git-sync-events"
import { asRecord, asString } from "@/lib/opencode-runtime-shared"
import { useSessionUI } from "@/stores/session-ui"

export type SessionGitExecOperation =
  | "push"
  | "pull"
  | "sync"
  | "fetch"
  | "merge-base"

export interface SessionGitExecResult {
  operation: SessionGitExecOperation
  ok: boolean
  error: string | null
  errorCode: string | null
  behind: number | null
  exitCode: number | null
  stdout: string | null
  stderr: string | null
}

function asOptionalText(value: unknown): string | null {
  return typeof value === "string" ? value : null
}

function asInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null
  }

  return Math.trunc(value)
}

function asNonNegativeInteger(value: unknown): number | null {
  const normalized = asInteger(value)
  if (normalized === null || normalized < 0) {
    return null
  }

  return normalized
}

function normalizeSessionGitExecOperation(value: unknown): SessionGitExecOperation | null {
  return value === "push" ||
      value === "pull" ||
      value === "sync" ||
      value === "fetch" ||
      value === "merge-base"
    ? value
    : null
}

export async function readSessionGitExecResult(args: {
  response: Response
  requestedOperation: SessionGitExecOperation
}): Promise<SessionGitExecResult> {
  let payload: Record<string, unknown> | null = null
  try {
    payload = asRecord(await args.response.json())
  } catch {
    payload = null
  }

  const normalizedResult = normalizeSessionGitExecResult({
    message: payload,
    fallbackOperation: args.requestedOperation,
  })
  if (normalizedResult) {
    return normalizedResult
  }

  return {
    operation: args.requestedOperation,
    ok: args.response.ok,
    error: args.response.ok ? null : (asString(payload?.error) ?? "Git operation failed"),
    errorCode: asString(payload?.errorCode),
    behind: asNonNegativeInteger(payload?.behind),
    exitCode: asInteger(payload?.exit_code),
    stdout: asOptionalText(payload?.stdout),
    stderr: asOptionalText(payload?.stderr),
  }
}

export function applySessionGitExecResult(args: {
  sessionId: string
  result: SessionGitExecResult
}): void {
  const ui = useSessionUI.getState()
  ui.setGitOperationLoading(false)
  ui.setGitOperationError(args.result.error)

  if (!args.result.ok) {
    return
  }

  dispatchSessionGitResult({
    sessionId: args.sessionId,
    operation: args.result.operation,
    ok: true,
  })
}

export function normalizeSessionGitExecResult(args: {
  message: unknown
  fallbackOperation?: SessionGitExecOperation
}): SessionGitExecResult | null {
  const payload = asRecord(args.message)
  if (!payload) {
    return null
  }

  const operation = normalizeSessionGitExecOperation(payload.operation) ?? args.fallbackOperation ?? null
  if (!operation || typeof payload.ok !== "boolean") {
    return null
  }

  return {
    operation,
    ok: payload.ok,
    error: payload.ok ? null : (asString(payload.error) ?? "Git operation failed"),
    errorCode: asString(payload.errorCode),
    behind: asNonNegativeInteger(payload.behind),
    exitCode: asInteger(payload.exit_code),
    stdout: asOptionalText(payload.stdout),
    stderr: asOptionalText(payload.stderr),
  }
}
